import * as formatParse from 'common/format-parse';
import SafeMath from 'common/safeMath';
import { Format } from 'nomst/Stores/Utility/format';
import { currencyConversion } from 'utils/MobxDataManager/singletons';
import { AmountCode } from 'typings/enums';
import { observable, computed, action, makeObservable } from 'mobx';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';

export class ManagerBudget {
    @observable employeeSourceId: string;
    @observable isoCurrencyCode: string;
    @observable sourceCountryCode: string;
    @observable displayName: string;
    @observable originalBudget: number;
    @observable adjustedBudget: number;
    @observable adjustedBudgetBeforeProration: number;
    @observable initialAdjustedBudget: number;
    @observable spent: number;
    @observable employeesTotalCurrentPay: number;
    @observable employeesTotalProratedCurrentPay: number;
    @observable adjustedBudgetInput: string;
    @observable adjustedBudgetTextInternal: string;
    @observable adjustedBudgetPercentageInput: string;
    @observable adjustedBudgetPercentageTextInternal: string;
    @observable errorMessageText: Nullable<string>;
    @observable maxPercentageValue: number; //999
    @observable isUnitValue: boolean;
    @observable department: string;
    @observable workLocation: string;
    @observable businessUnit: string;

    _currentBudget: CycleBudget;

    _format: Format;

    constructor(dto: IManagerBudget, format: Format, currentBudget: CycleBudget) {
        makeObservable(this);
        this.employeeSourceId = dto.employeeSourceId;
        this.isoCurrencyCode = dto.isoCurrencyCode;
        this.sourceCountryCode = dto.sourceCountryCode;
        this.displayName = dto.displayName;
        this.originalBudget = dto.originalBudget;
        this.adjustedBudget = dto.adjustedBudget;
        this.adjustedBudgetBeforeProration = dto.adjustedBudgetBeforeProration;
        this.initialAdjustedBudget = dto.initialAdjustedBudget;
        this.spent = dto.spent || 0;
        this.employeesTotalCurrentPay = dto.employeesTotalCurrentPay;
        this.employeesTotalProratedCurrentPay = dto.employeesTotalProratedCurrentPay;
        this.adjustedBudgetInput = dto.adjustedBudgetInput;
        this.adjustedBudgetTextInternal = dto.adjustedBudgetTextInternal;
        this.adjustedBudgetPercentageInput = dto.adjustedBudgetPercentageInput;
        this.adjustedBudgetPercentageTextInternal = dto.adjustedBudgetPercentageTextInternal;
        this.errorMessageText = dto.errorMessageText || '';
        this.maxPercentageValue = dto.maxPercentageValue || 999;
        this.isUnitValue = dto.isUnitValue;
        this.department = dto.department;
        this.workLocation = dto.workLocation;
        this.businessUnit = dto.businessUnit;

        this._currentBudget = currentBudget;
        this._format = format;
        this.setInitialBudget();
    }

    @computed
    get isStockBudget(): boolean {
        return this._currentBudget.cycleBudgetFields.some(budgetField => budgetField.amountCode === AmountCode.Stock);
    }

    getCurrencyCodeToUse(): string {
        if (!currencyConversion.selectedCurrency || currencyConversion.selectedCurrency === 'Multiple') {
            return this.isoCurrencyCode;
        }
        return currencyConversion.selectedCurrency;
    }

    getConvertedValueToDisplay(value: number): string {
        return (this.isUnitValue)
            ? this._format.formatStockDecimal(value)
            : currencyConversion.convertCurrencyToTextDisplay(
                this.isoCurrencyCode,
                this._format, 
                value,
                false,
                this.getCurrencyCodeToUse());
    }

    @computed
    get budgetProrateRatio(): number {
        return SafeMath.divide(this.employeesTotalProratedCurrentPay, this.employeesTotalCurrentPay);
    }

    getAdjustedBudgetPercentage(value: number): number {
        return SafeMath.divide(value * 100, this.employeesTotalCurrentPay);
    }

    @computed
    get adjustedBudgetPercentage(): number {
        return this.getAdjustedBudgetPercentage(this.adjustedBudget);
    }

    @computed
    get proratedBudget(): number {
        return this.adjustedBudgetBeforeProration * this.budgetProrateRatio;
    }

    @computed
    get budgetBalance(): number {
        return this.adjustedBudget - this.spent;
    }


    getAdjustedAmountText() {
        if (this.adjustedBudget < 0) {
            return '';
        }
        return this.getConvertedValueToDisplay(this.adjustedBudget);
    }

    getAdjustedPercentageText() {
        if (this.adjustedBudgetPercentage < 0) {
            return '';
        }
        return this._format.formatPercentage(this.adjustedBudgetPercentage);
    }

    @computed
    get employeesTotalCurrentPayText(): string {
        return currencyConversion.convertCurrencyToTextDisplay(
            this.isoCurrencyCode,
            this._format,
            this.employeesTotalCurrentPay,
            false,
            this.getCurrencyCodeToUse());
    }

    @computed
    get originalBudgetText(): string {
        return this.getConvertedValueToDisplay(this.originalBudget);
    }

    @computed
    get spentText(): string {
        return this.getConvertedValueToDisplay(this.spent);
    }

    @computed
    get adjustedBudgetText(): string {
        if (this.adjustedBudgetTextInternal != null) {
            return this.adjustedBudgetTextInternal;
        }

        return this.getAdjustedAmountText();
    }

    @computed
    get adjustedBudgetBeforeProrationText(): string {
        return this.getConvertedValueToDisplay(this.adjustedBudgetBeforeProration);
    }

    @computed
    get proratedBudgetText(): string {
        return this.getConvertedValueToDisplay(this.proratedBudget);
    }

    @computed
    get adjustedBudgetPercentageText(): string {
        if (this.adjustedBudgetPercentageTextInternal != null) {
            return this.adjustedBudgetPercentageTextInternal;
        }

        return this.getAdjustedPercentageText();
    }

    @computed
    get budgetBalanceText(): string {
        const budgetBalance = Math.abs(this.budgetBalance);
        const text = this.getConvertedValueToDisplay(budgetBalance);
        return (this.budgetBalance < 0) ? `(${text})` : text;
    }

    @computed
    get budgetBalanceClass(): string {
        return (this.budgetBalance < 0) ? 'pxl-text-c10' : '';
    }

    @action
    clearErrorMessage(): void {
        this.errorMessageText = null;
    }

    @action
    formatAmountText(): void {
        this.adjustedBudgetTextInternal = this.getAdjustedAmountText();
    }

    @action
    formatPercentageText(): void {
        this.adjustedBudgetPercentageTextInternal = this.getAdjustedPercentageText();
    }

    @action
    changeBudgetByPercentage(
        percentage: number,
        overridePercentageText: boolean = false): void {
        const percentageRatio = percentage / 100;
        this.adjustedBudget = this.employeesTotalCurrentPay * percentageRatio;
        // make amount reflect percentage change
        this.formatAmountText();
        if (overridePercentageText) {
            this.formatPercentageText();
        }
    }

    @action
    changeBudgetByValue(
        value: number,
        overrideAmountText: boolean = false): void {
        this.adjustedBudget = value;
        // make percentage reflect amount change
        this.formatPercentageText();
        if (overrideAmountText) {
            this.formatAmountText();
        }
    }

    @action
    setInitialBudget(): void {
        this.initialAdjustedBudget = this.adjustedBudget;
        this.adjustedBudgetBeforeProration =
            SafeMath.divide(this.adjustedBudget, this.budgetProrateRatio);
    }

    @action
    adjustBudgetToUseProration(): void {
        this.adjustedBudgetBeforeProration = this.adjustedBudget;
        this.changeBudgetByValue(this.proratedBudget, true);
    }

    @action
    adjustBudgetToNotUseProration(): void {
        this.changeBudgetByValue(this.adjustedBudgetBeforeProration, true);
    }


    afterCreate(): void {
        this.setInitialBudget();
    }

    @action
    changeBudgetValue(valueText: string): void {
        this.clearErrorMessage();
        this.adjustedBudgetTextInternal = valueText;
        let value = formatParse.parseValueOrDefault(valueText, 0);
        if (this.isUnitValue === false) {
            value = currencyConversion.convertCurrencyFromDisplay(
                this.isoCurrencyCode,
                value,
                false,
                this.getCurrencyCodeToUse());
        }

        this.changeBudgetByValue(value);
    }

    @action
    blurAdjustedBudgetInput(): void {
        this.clearErrorMessage();
        this.formatAmountText();
    }

    @action
    blurProratedBudgetInput(): void {
        this.clearErrorMessage();
        this.formatAmountText();
    }

    @action
    changeBudgetPercentage(percentageText: string): void {
        this.clearErrorMessage();
        this.adjustedBudgetPercentageTextInternal = percentageText;
        const percentage = formatParse.parseValueOrDefault(percentageText, 0);
        this.changeBudgetByPercentage(percentage);
    }

    @action
    blurAdjustedBudgetPercentageInput(): void {
        this.clearErrorMessage();
        this.formatPercentageText();
    }

    @action
    blurProratedBudgetPercentageInput(): void {
        this.clearErrorMessage();
        this.formatPercentageText();
    }
}